import { useState, useCallback } from 'react';
import { Button, Skeleton, Checkbox, Label } from '@ui';
import { Plus, Package, CreditCard, LayoutGrid } from 'lucide-react';
import { toastMessage } from '@ui/components/Toast';
import { useGroupStore } from '@/stores/groupStore';
import { cn } from '@ui/lib/utils';
import {
    useGetPricingPlans,
    useTogglePricingPlan,
    useDeletePricingPlan,
} from '../../pricing-plans/hooks';
import {
    PricingPlanModal,
    ToggleConfirmDialog,
    DeleteConfirmDialog,
    PlanCard,
} from '../../pricing-plans/components';
import type { PricingPlan, PricingPlanType } from '../../pricing-plans/types';

type FilterType = 'ALL' | PricingPlanType;

const FILTER_OPTIONS: { type: FilterType; icon: typeof LayoutGrid; label: string }[] = [
    { type: 'ALL', icon: LayoutGrid, label: '전체' },
    { type: 'PACKAGE', icon: Package, label: '횟수제' },
    { type: 'PREPAID', icon: CreditCard, label: '선불' },
];

export function PricingPlanTab() {
    const [filterType, setFilterType] = useState<FilterType>('ALL');
    const [showInactive, setShowInactive] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState<PricingPlan | null>(null);
    const [togglingPlan, setTogglingPlan] = useState<PricingPlan | null>(null);
    const [deletingPlan, setDeletingPlan] = useState<PricingPlan | null>(null);

    const selectedGroup = useGroupStore(state => state.selectedGroup);

    // 전체 이용권 목록 조회 (카운트용)
    const { data: allPlans } = useGetPricingPlans({
        request: {
            groupId: selectedGroup?.id,
            withInactive: showInactive,
        },
    });

    // 필터링된 이용권 목록 조회
    const { data: plans, isLoading } = useGetPricingPlans({
        request: {
            groupId: selectedGroup?.id,
            type: filterType === 'ALL' ? undefined : filterType,
            withInactive: showInactive,
        },
    });

    // 토글 mutation
    const { mutate: togglePlan, isPending: isToggling } = useTogglePricingPlan({
        options: {
            onSuccess: (data) => {
                toastMessage({
                    message: data.active ? '이용권이 활성화되었습니다.' : '이용권이 비활성화되었습니다.',
                    type: 'success',
                });
                setTogglingPlan(null);
            },
            onError: (error) => {
                toastMessage({ message: error.message || '상태 변경에 실패했습니다.', type: 'error' });
            },
        },
    });

    // 삭제 mutation
    const { mutate: deletePlan, isPending: isDeleting } = useDeletePricingPlan({
        options: {
            onSuccess: () => {
                toastMessage({ message: '이용권이 삭제되었습니다.', type: 'success' });
                setDeletingPlan(null);
            },
            onError: (error) => {
                toastMessage({ message: error.message || '삭제에 실패했습니다.', type: 'error' });
            },
        },
    });

    // 핸들러들
    const handleEdit = useCallback((plan: PricingPlan) => {
        setEditingPlan(plan);
    }, []);

    const handleToggle = useCallback((plan: PricingPlan) => {
        setTogglingPlan(plan);
    }, []);

    const handleDelete = useCallback((plan: PricingPlan) => {
        setDeletingPlan(plan);
    }, []);

    const handleConfirmToggle = useCallback(() => {
        if (togglingPlan && selectedGroup?.id) {
            togglePlan({
                groupId: selectedGroup.id,
                planId: togglingPlan.id,
            });
        }
    }, [togglingPlan, selectedGroup?.id, togglePlan]);

    const handleConfirmDelete = useCallback(() => {
        if (deletingPlan && selectedGroup?.id) {
            deletePlan({
                groupId: selectedGroup.id,
                planId: deletingPlan.id,
            });
        }
    }, [deletingPlan, selectedGroup?.id, deletePlan]);

    // 타입별 이용권 개수 카운트 (전체 목록 기준)
    const typeCounts: Partial<Record<FilterType, number>> = allPlans?.reduce((acc, plan) => {
        acc[plan.type] = (acc[plan.type] || 0) + 1;
        acc.ALL = (acc.ALL || 0) + 1;
        return acc;
    }, {} as Partial<Record<FilterType, number>>) || {};

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                    <Skeleton className="h-40" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">이용권 템플릿</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">지점에서 사용할 이용권 템플릿을 관리합니다.</p>
                </div>
                <Button onClick={() => setIsCreateModalOpen(true)} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    이용권 추가
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                {/* 타입 필터 */}
                <div className="flex flex-wrap gap-2">
                    {FILTER_OPTIONS.map(({ type, icon: Icon, label }) => (
                        <button
                            key={type}
                            type="button"
                            onClick={() => setFilterType(type)}
                            className={cn(
                                'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                                filterType === type
                                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 border border-orange-200'
                                    : 'bg-white dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 border border-transparent hover:bg-neutral-100 dark:hover:bg-neutral-800'
                            )}
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                            {typeCounts[type] !== undefined && (
                                <span className={cn(
                                    'px-1.5 py-0.5 text-xs rounded-full',
                                    filterType === type
                                        ? 'bg-orange-200 text-orange-700'
                                        : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300'
                                )}>
                                    {typeCounts[type] || 0}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* 비활성 포함 체크박스 */}
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="show-inactive-pricing"
                        checked={showInactive}
                        onCheckedChange={(checked) => setShowInactive(checked as boolean)}
                    />
                    <Label htmlFor="show-inactive-pricing" className="text-sm cursor-pointer">
                        비활성 포함
                    </Label>
                </div>
            </div>

            {/* Plan List */}
            <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                    {plans?.length || 0}개의 이용권
                </p>
                {plans && plans.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {plans.map(plan => (
                            <PlanCard
                                key={plan.id}
                                plan={plan}
                                onEdit={handleEdit}
                                onToggle={handleToggle}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 border rounded-lg bg-white dark:bg-neutral-900">
                        <Package className="mx-auto h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-4" />
                        <p className="text-neutral-500 dark:text-neutral-400 mb-4">
                            {filterType !== 'ALL'
                                ? '해당 타입의 이용권이 없습니다.'
                                : '등록된 이용권이 없습니다.'}
                        </p>
                        <Button variant="outline" onClick={() => setIsCreateModalOpen(true)} size="sm">
                            <Plus className="mr-2 h-4 w-4" />
                            첫 이용권 추가하기
                        </Button>
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            {selectedGroup?.id && (
                <PricingPlanModal
                    isOpen={isCreateModalOpen || !!editingPlan}
                    onClose={() => {
                        setIsCreateModalOpen(false);
                        setEditingPlan(null);
                    }}
                    groupId={selectedGroup.id}
                    plan={editingPlan}
                />
            )}

            {/* Toggle Confirmation Dialog */}
            <ToggleConfirmDialog
                isOpen={!!togglingPlan}
                onClose={() => setTogglingPlan(null)}
                plan={togglingPlan}
                onConfirm={handleConfirmToggle}
                isPending={isToggling}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                isOpen={!!deletingPlan}
                onClose={() => setDeletingPlan(null)}
                plan={deletingPlan}
                onConfirm={handleConfirmDelete}
                isPending={isDeleting}
            />
        </div>
    );
}
